import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';

import { FeedbackModal } from './FeedbackModal';

interface SummaryFeedbackProps {
  sessionId: string;
  templateId: number | null;
  summary: string;
  transcription?: string;
}

type VoteRating = 'positive' | 'negative' | null;

const MAX_TRANSCRIPTION_EXCERPT_LENGTH = 1000;

interface VoteStatus {
  has_voted: boolean;
  rating?: 'positive' | 'negative';
  voted_at?: string;
}

async function fetchVoteStatus(sessionId: string, templateId: number): Promise<VoteStatus> {
  const params = new URLSearchParams({
    session_id: sessionId,
    template_id: templateId.toString(),
  });

  const response = await fetch(`/api/template-feedback/vote-status?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch vote status');
  }

  return response.json();
}

async function submitVote(params: {
  sessionId: string;
  templateId: number;
  rating: 'positive' | 'negative';
  summary: string;
  transcription?: string;
}): Promise<void> {
  const response = await fetch('/api/template-feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      session_id: params.sessionId,
      template_id: params.templateId,
      rating: params.rating,
      voted_on_summary_text: params.summary,
      transcription_excerpt: params.transcription?.slice(0, MAX_TRANSCRIPTION_EXCERPT_LENGTH),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to submit vote');
  }
}

async function deleteVote(sessionId: string, templateId: number): Promise<void> {
  const response = await fetch(`/api/template-feedback/${sessionId}/${templateId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete vote');
  }
}

export const SummaryFeedback = ({ sessionId, templateId, summary, transcription }: SummaryFeedbackProps) => {
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const queryKey = ['vote-status', sessionId, templateId];

  const { data: voteStatus, isLoading: isInitialLoading } = useQuery({
    queryKey,
    queryFn: () => {
      if (!templateId) throw new Error('templateId is required');
      return fetchVoteStatus(sessionId, templateId);
    },
    enabled: !!sessionId && !!templateId,
  });

  const voteState: VoteRating = voteStatus?.has_voted && voteStatus.rating ? voteStatus.rating : null;

  const submitMutation = useMutation({
    mutationFn: (rating: 'positive' | 'negative') => {
      if (!templateId) throw new Error('templateId is required');
      return submitVote({ sessionId, templateId, rating, summary, transcription });
    },
    onMutate: async (rating) => {
      await queryClient.cancelQueries({ queryKey });
      const previousStatus = queryClient.getQueryData<VoteStatus>(queryKey);
      queryClient.setQueryData<VoteStatus>(queryKey, { has_voted: true, rating });
      return { previousStatus };
    },
    onError: (_err, _rating, context) => {
      if (context?.previousStatus) {
        queryClient.setQueryData(queryKey, context.previousStatus);
      }
      toast({
        title: 'Villa',
        description: 'Ekki tókst að vista atkvæði. Reyndu aftur.',
        variant: 'destructive',
      });
    },
    onSuccess: (_data, rating) => {
      if (rating === 'negative') {
        setFeedbackModalOpen(true);
      } else {
        toast({
          title: 'Þakka þér fyrir endurgjöfina',
          description: 'Endurgjöfin gerir samantektina betri yfir tíma.',
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!templateId) throw new Error('templateId is required');
      return deleteVote(sessionId, templateId);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previousStatus = queryClient.getQueryData<VoteStatus>(queryKey);
      queryClient.setQueryData<VoteStatus>(queryKey, { has_voted: false });
      return { previousStatus };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousStatus) {
        queryClient.setQueryData(queryKey, context.previousStatus);
      }
      toast({
        title: 'Villa',
        description: 'Ekki tókst að fjarlægja atkvæði. Reyndu aftur.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Atkvæði fjarlægt',
        description: 'Þakka þér fyrir endurgjöfina.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const isLoading = submitMutation.isPending || deleteMutation.isPending;

  const handleVote = (rating: 'positive' | 'negative') => {
    if (!sessionId || !templateId || isLoading) return;

    const isTogglingOff = voteState === rating;

    if (isTogglingOff) {
      deleteMutation.mutate();
    } else {
      submitMutation.mutate(rating);
    }
  };

  // Don't render if missing required props
  if (!sessionId || !templateId || !summary) {
    return null;
  }

  // Show skeleton while loading initial state
  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-2">
        <div className="h-3 w-24 animate-pulse rounded bg-slate-100" />
        <div className="h-6 w-6 animate-pulse rounded bg-slate-100" />
        <div className="h-6 w-6 animate-pulse rounded bg-slate-100" />
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-center gap-3 pt-2 pb-4">
        <span className="text-muted-foreground/70 text-sm">Hvernig fannst þér samantektin?</span>
        <button
          type="button"
          disabled={isLoading}
          onClick={() => handleVote('positive')}
          className={cn(
            'cursor-pointer rounded-full p-2 transition-all duration-200 disabled:cursor-not-allowed',
            'hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-200',
            voteState === 'positive' ? 'bg-green-100 text-green-600' : 'text-slate-400 hover:text-green-500'
          )}
          aria-label="Góð samantekt"
          title="Góð samantekt"
        >
          {isLoading && voteState !== 'positive' ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <ThumbsUp className={cn('h-5 w-5', voteState === 'positive' && 'fill-current')} />
          )}
        </button>
        <button
          type="button"
          disabled={isLoading}
          onClick={() => handleVote('negative')}
          className={cn(
            'cursor-pointer rounded-full p-2 transition-all duration-200 disabled:cursor-not-allowed',
            'hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200',
            voteState === 'negative' ? 'bg-red-100 text-red-600' : 'text-slate-400 hover:text-red-500'
          )}
          aria-label="Þarf að bæta"
          title="Þarf að bæta"
        >
          {isLoading && voteState !== 'negative' ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <ThumbsDown className={cn('h-5 w-5', voteState === 'negative' && 'fill-current')} />
          )}
        </button>
      </div>

      {feedbackModalOpen && templateId && (
        <FeedbackModal
          open={true}
          onOpenChange={setFeedbackModalOpen}
          templateId={templateId}
          sessionId={sessionId}
          onFeedbackSubmitted={() => {
            toast({ title: 'Takk fyrir endurgjöfina!' });
          }}
        />
      )}
    </>
  );
};
